'use client'

import { ReactNode, useState } from 'react'
import { IconBuilding, IconHome2 } from '@tabler/icons-react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { getQueryClient, QueryKey } from 'shared/api'
import Button from 'shared/ui/Button'
import { Modal, ModalTitle } from 'shared/ui/Modal'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'

import { extractTokenFromClientSession } from '../../../../session'
import { deleteDeliveryAddress } from '../../api'
import { CustomerAddress, ResponseStatus } from '../../types'

import styles from './ModalRemoveDeliveryAddress.module.scss'

type ModalRemoveDeliveryAddressTypes = {
  trigger: ReactNode
  address: CustomerAddress
}

const ModalRemoveDeliveryAddress = ({
  trigger,
  address
}: ModalRemoveDeliveryAddressTypes) => {
  const [open, setOpen] = useState(false)
  const [submitState, setSubmitState] = useState<ResponseStatus>(
    ResponseStatus.initial
  )

  const router = useRouter()
  const pathname = usePathname()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleConfirmClick = async () => {
    if (submitState !== ResponseStatus.pending) {
      await onSubmit()
    }
  }

  const _handleClose = async () => {
    /**
     * Refresh props from getServerSideProps
     * More: https://www.joshwcomeau.com/nextjs/refreshing-server-side-props/
     */
    void router.replace(pathname || '')
    handleClose()
  }

  const onSubmit = async () => {
    if (!address) {
      setSubmitState(ResponseStatus.rejected)
    }

    setSubmitState(ResponseStatus.pending)

    try {
      await deleteDeliveryAddress(address.id, token)

      getQueryClient().setQueryData(
        [QueryKey.deliveryAddresses, token],
        (oldData: CustomerAddress[] | undefined) => {
          return (
            oldData?.filter(
              (addressItem: CustomerAddress) => addressItem.id !== address.id
            ) || []
          )
        }
      )

      await _handleClose()

      toast.success(`Adres "${address.name}" został usunięty.`)
      setSubmitState(ResponseStatus.fulfilled)
    } catch (error) {
      toast.error(getApiErrors(error))
      setSubmitState(ResponseStatus.rejected)
    }
  }

  return (
    <>
      <div
        className={styles.triggerWrapper}
        onKeyDown={e => {
          if (e.key === KEYBOARD_KEYS.ENTER) {
            e.preventDefault()
            e.stopPropagation()
            void handleOpen()
          }
        }}
        onClick={e => {
          e.stopPropagation()
          void handleOpen()
        }}
        role='button'
        tabIndex={0}
        aria-label='Usuń adres'
      >
        {trigger}
      </div>
      <Modal
        id='remove-delivery-address-modal'
        open={open}
        onClose={handleClose}
        size='small'
      >
        <ModalTitle>Potwierdź usunięcie adresu</ModalTitle>
        <div className={styles.summaryWrapper}>
          <div
            className={styles.icon}
            color={address.iconColor || ''}
            style={{ '--icon-color': address.iconColor || '' } as React.CSSProperties}
          >
            {address.iconType === 'home' ? <IconHome2 /> : <IconBuilding />}
          </div>
          <div>
            <div className={styles.addressName}>{address.name}</div>
            <p>
              {address?.street} {address?.houseNumber}{' '}
              {address?.flatNumber ? `m. ${address.flatNumber}` : null}
            </p>
          </div>
        </div>
        <div className={styles.buttonsWrapper}>
          <Button
            fullWidth
            type='button'
            disabled={submitState === ResponseStatus.pending}
            onClick={e => {
              e.stopPropagation()
              void handleConfirmClick()
            }}
          >
            Potwierdź
          </Button>
          <Button
            fullWidth
            variant='neutralOutline'
            onClick={e => {
              e.stopPropagation()
              handleClose()
            }}
          >
            Anuluj
          </Button>
        </div>
      </Modal>
    </>
  )
}

export default ModalRemoveDeliveryAddress
